import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import usuariosApiService from '../services/usuariosApiService';
import { sociasApiService } from '../services/sociasApiService';
import '../styles/UserManagement.css';

const RegisterUser = () => {
  const navigate = useNavigate();
  const [socias, setSocias] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    sociaId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    permissions: [],
    permissionsIds: []
  });

  // Cargar socias disponibles y permisos
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar todas las socias
      let sociasData = await sociasApiService.getSocias();
      console.log('Socias obtenidas RAW:', sociasData);
      
      // Si viene en un objeto con propiedad data, extraer el array
      if (sociasData && !Array.isArray(sociasData)) {
        sociasData = sociasData.data || sociasData.socias || [];
      }
      
      // Asegurarse de que sea un array
      if (!Array.isArray(sociasData)) {
        sociasData = [];
      }
      
      console.log('Socias array:', sociasData);
      console.log('Total socias:', sociasData.length);
      
      // Cargar todos los usuarios existentes
      const usuariosData = await usuariosApiService.getAllUsuarios();
      console.log('Usuarios obtenidos:', usuariosData);
      
      // Filtrar socias que NO tienen usuario
      const sociasConUsuario = usuariosData.map(u => u.sociaId).filter(id => id != null);
      console.log('IDs de socias con usuario:', sociasConUsuario);
      
      const sociasDisponibles = sociasData.filter(s => {
        const sociaId = s.sociaId || s.SOCIA_ID || s.id;
        const tieneUsuario = sociasConUsuario.includes(sociaId);
        console.log(`Socia ${sociaId}:`, s);
        console.log(`  - Tiene usuario: ${tieneUsuario}`);
        return !tieneUsuario;
      });
      console.log('Socias disponibles después del filtro:', sociasDisponibles);
      console.log('Total socias disponibles:', sociasDisponibles.length);
      
      setSocias(sociasDisponibles);
      
      // Cargar permisos disponibles
      const permisosData = await usuariosApiService.getPermisosDisponibles();
      
      // Mapear permisos con descripciones
      const permisosFormateados = permisosData.map(p => ({
        id: p.PERMISO_ID || p.id,
        label: p.PERMISO_NOMBRE || p.nombre,
        description: getPermissionDescription(p.PERMISO_NOMBRE || p.nombre)
      }));
      
      setAvailablePermissions(permisosFormateados);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar los datos necesarios');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionDescription = (permissionName) => {
    const descriptions = {
      'Administrador': 'Acceso completo a todas las funcionalidades del sistema',
      'Gestionar Socias': 'Crear, editar y visualizar información de socias',
      'Ver Socias': 'Solo visualizar información de socias',
      'Gestionar Inventario': 'Administrar productos y categorías del inventario',
      'Ver Inventario': 'Solo visualizar el inventario',
      'Gestionar Requisitos': 'Administrar requisitos de ingreso',
      'Ver Requisitos': 'Solo visualizar requisitos',
      'Gestionar Tratamientos': 'Administrar tratamientos médicos',
      'Ver Tratamientos': 'Solo visualizar tratamientos',
      'Gestionar Hospitales': 'Administrar información de hospitales',
      'Ver Hospitales': 'Solo visualizar hospitales',
      'Gestionar Mentores': 'Administrar información de mentores',
      'Ver Mentores': 'Solo visualizar mentores',
      'Gestionar Médicos': 'Administrar información de médicos',
      'Ver Médicos': 'Solo visualizar médicos',
      'Gestionar Usuarios': 'Administrar cuentas de usuario',
      'Gestionar Formato Carnet': 'Administrar formato de carnets',
      'Generar Reportes': 'Generar reportes del sistema',
      'Configuración Sistema': 'Acceso a configuración del sistema'
    };
    return descriptions[permissionName] || 'Permiso del sistema';
  };

  const handleSociaChange = (e) => {
    const sociaId = e.target.value;
    setFormData(prev => ({ ...prev, sociaId }));
    
    if (sociaId) {
      // Buscar la socia seleccionada y llenar los campos
      const sociaSeleccionada = socias.find(s => {
        const id = s.sociaId || s.SOCIA_ID || s.id;
        return id && id.toString() === sociaId.toString();
      });
      
      console.log('Socia seleccionada:', sociaSeleccionada);
      
      if (sociaSeleccionada) {
        setFormData(prev => ({
          ...prev,
          firstName: sociaSeleccionada.sociaNombre || sociaSeleccionada.SOCIA_NOMBRE || '',
          lastName: sociaSeleccionada.sociaApellido || sociaSeleccionada.SOCIA_APELLIDO || '',
          email: sociaSeleccionada.sociaEmail || sociaSeleccionada.SOCIA_EMAIL || '',
          phone: sociaSeleccionada.sociaTelefono1 || sociaSeleccionada.SOCIA_TELEFONO1 || ''
        }));
      }
    } else {
      // Limpiar campos si no hay socia seleccionada
      setFormData(prev => ({
        ...prev,
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      }));
    }
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => {
      const isCurrentlySelected = prev.permissionsIds.includes(permissionId);
      const newPermissionsIds = isCurrentlySelected
        ? prev.permissionsIds.filter(p => p !== permissionId)
        : [...prev.permissionsIds, permissionId];
      
      // Actualizar también los nombres de permisos
      const newPermissions = availablePermissions
        .filter(opt => newPermissionsIds.includes(opt.id))
        .map(opt => opt.label);
      
      return {
        ...prev,
        permissionsIds: newPermissionsIds,
        permissions: newPermissions
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sociaId) {
      alert('Debe seleccionar una socia');
      return;
    }
    
    if (formData.permissionsIds.length === 0) {
      alert('Debe seleccionar al menos un permiso');
      return;
    }
    
    try {
      console.log('Datos a enviar:', {
        sociaId: formData.sociaId,
        permisosIds: formData.permissionsIds
      });
      
      // Registrar usuario con la socia seleccionada y los permisos
      await usuariosApiService.registrarUsuario(formData.sociaId, formData.permissionsIds);
      
      alert('Usuario registrado exitosamente');
      navigate('/user-management');
    } catch (error) {
      console.error('Error al registrar usuario:', error);
      console.error('Respuesta del servidor:', error.response?.data);
      alert('Error al registrar el usuario: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancel = () => {
    navigate('/user-management');
  };

  if (loading) {
    return (
      <div className="register-user-container">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="register-user-container">
      <div className="register-header">
        <h1>Registrar Nuevo Usuario</h1>
        <p>Seleccione una socia y asigne los permisos correspondientes</p>
      </div>

      <form onSubmit={handleSubmit} className="register-form">
        <div className="form-section">
          <h3>Selección de Socia</h3>
          
          <div className="form-group">
            <label htmlFor="sociaId">Socia * (Total disponibles: {socias.length})</label>
            
            {/* Campo que muestra solo el ID cuando hay selección */}
            {formData.sociaId ? (
              <div style={{ marginBottom: '10px' }}>
                <input
                  type="text"
                  value={formData.sociaId}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    cursor: 'not-allowed'
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      sociaId: '',
                      firstName: '',
                      lastName: '',
                      email: '',
                      phone: ''
                    }));
                  }}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  <i className="fas fa-times"></i> Cambiar Socia
                </button>
              </div>
            ) : (
              <select
                id="sociaId"
                name="sociaId"
                value={formData.sociaId}
                onChange={handleSociaChange}
                required
              >
                <option value="">-- Seleccione una socia --</option>
                {socias.map((socia, index) => {
                  const id = socia.sociaId || socia.SOCIA_ID || socia.id;
                  const nombre = socia.sociaNombre || socia.SOCIA_NOMBRE || '';
                  const apellido = socia.sociaApellido || socia.SOCIA_APELLIDO || '';
                  const cedula = socia.sociaCedula || socia.SOCIA_CEDULA || '';
                  
                  return (
                    <option key={id || index} value={id}>
                      {nombre} {apellido} - {cedula}
                    </option>
                  );
                })}
              </select>
            )}
            
            {socias.length === 0 && (
              <p className="help-text warning">
                <i className="fas fa-exclamation-triangle"></i>
                No hay socias disponibles. Todas las socias ya tienen usuario asignado.
              </p>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Información Personal</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">Nombre(s)</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                readOnly
                disabled
                placeholder="Seleccione una socia"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">Apellidos</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                readOnly
                disabled
                placeholder="Seleccione una socia"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Correo Electrónico</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                readOnly
                disabled
                placeholder="Seleccione una socia"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Teléfono</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                readOnly
                disabled
                placeholder="Seleccione una socia"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Permisos de Módulo</h3>
          
          <div className="permissions-section">
            {!formData.sociaId && (
              <div className="blocked-section-message">
                <i className="fas fa-lock"></i>
                <p>Seleccione una socia para configurar los permisos</p>
              </div>
            )}
            {availablePermissions.map(permission => (
              <div key={permission.id} className={`permission-option ${!formData.sociaId ? 'disabled' : ''}`}>
                <label className="permission-label">
                  <input
                    type="checkbox"
                    checked={formData.permissionsIds.includes(permission.id)}
                    onChange={() => handlePermissionChange(permission.id)}
                    disabled={!formData.sociaId}
                  />
                  <span className="checkmark"></span>
                  <div className="permission-info">
                    <strong>{permission.label}</strong>
                    <p>{permission.description}</p>
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={handleCancel}>
            <i className="fas fa-times"></i>
            Cancelar
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            disabled={!formData.sociaId || formData.permissionsIds.length === 0}
          >
            <i className="fas fa-user-plus"></i>
            Registrar Usuario
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterUser;
